import { z } from 'zod';
import { CartItemSchema } from './CartItem';
import { OrderDeliveryAddressComponentsSchema } from '../orders/Order';

/** A failed/aborted checkout attempt recorded on the cart (see ICheckoutFailure). */
export const CheckoutFailureSchema = z.object({
    reason: z.string(),
    code: z.string().optional(),
    at: z.date(),
});

export const CartSchema = z.object({
    cartId: z.number().int().positive(),
    guid: z.string().min(1),
    customerId: z.string().optional(),
    restaurantId: z.number().int().positive(),
    tenantId: z.number().int().positive(),
    items: z.array(CartItemSchema),
    orderType: z.enum(['pickup', 'delivery', 'dineIn']),
    // The table a dine-in cart belongs to — see ICart.tableGuid. Without it
    // here `CartSchema.parse` strips the field from every synced cart
    // document, and a guest's order reaches the kitchen with no table on it.
    tableGuid: z.string().min(1).max(128).optional(),
    scheduledTime: z.date().optional(),
    deliveryAddress: z.string().optional(),
    // Structured address — see ICart.deliveryAddressComponents. Without it here
    // `CartSchema.parse` strips the field from every synced cart document.
    deliveryAddressComponents: OrderDeliveryAddressComponentsSchema.optional(),
    configuredAt: z.date().optional(),
    createdAt: z.date(),
    updatedAt: z.date(),
    expiresAt: z.date().optional(),
    // Firestore sync fields
    syncedAt: z.date().optional(),
    version: z.number().int().min(1).default(1),
    isGuest: z.boolean().default(true),
    lastCheckoutFailure: CheckoutFailureSchema.optional(),
    checkoutFailureCount: z.number().int().min(0).optional(),
    // Guest code that sent this guest here — see ICart.guestCodeGuid.
    guestCodeGuid: z.string().min(1).max(128).optional(),
});

export const CartDataSchema = z.object({
    cartId: z.number().int().positive(),
    guid: z.string().min(1),
    customerId: z.string().optional(),
    restaurantId: z.number().int().positive(),
    tenantId: z.number().int().positive(),
    items: z.array(CartItemSchema).default([]),
    orderType: z.enum(['pickup', 'delivery', 'dineIn']).default('pickup'),
    // See CartSchema.tableGuid.
    tableGuid: z.string().min(1).max(128).optional(),
    scheduledTime: z.date().optional(),
    deliveryAddress: z.string().optional(),
    // Structured address — see ICart.deliveryAddressComponents. Without it here
    // `CartSchema.parse` strips the field from every synced cart document.
    deliveryAddressComponents: OrderDeliveryAddressComponentsSchema.optional(),
    configuredAt: z.date().optional(),
    createdAt: z.date().default(() => new Date()),
    updatedAt: z.date().default(() => new Date()),
    expiresAt: z.date().optional(),
    // Firestore sync fields
    syncedAt: z.date().optional(),
    version: z.number().int().min(1).optional().default(1),
    isGuest: z.boolean().optional().default(true),
    lastCheckoutFailure: CheckoutFailureSchema.optional(),
    checkoutFailureCount: z.number().int().min(0).optional(),
    // Guest code that sent this guest here — see ICart.guestCodeGuid.
    guestCodeGuid: z.string().min(1).max(128).optional(),
});

export type CartSchemaType = z.infer<typeof CartSchema>;
export type CartDataSchemaType = z.infer<typeof CartDataSchema>;
